import { FaxTypes } from '@frontend/api-fax';
import { useTranslation } from '@frontend/i18n';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Chip, SwitchChipGroup, Text, useSwitchChipGroup } from '@frontend/design-system';
import { useInboxFiltersShallowStore } from '../../stores/inbox-filters-store';

export const FaxStatusFilter = () => {
  const { t } = useTranslation('fax');

  const FILTER_OPTIONS: { value: FaxTypes.FaxStatusFilter; label: string }[] = [
    { value: FaxTypes.FaxStatusFilter.UNREAD, label: t('Unread') },
    { value: FaxTypes.FaxStatusFilter.READ, label: t('Read') },
    { value: FaxTypes.FaxStatusFilter.RECEIVED, label: t('Received') },
    { value: FaxTypes.FaxStatusFilter.SENT, label: t('Sent') },
  ];
  const { inboxFilters, setInboxFilters } = useInboxFiltersShallowStore('inboxFilters', 'setInboxFilters');
  const active = inboxFilters.statusFilter ?? FaxTypes.FaxStatusFilter.ALL;
  const isAllSelected = active === FaxTypes.FaxStatusFilter.ALL;

  const props = useSwitchChipGroup({
    onChange: (val) => {
      const selected = val.find((v) => v !== active) as FaxTypes.FaxStatusFilter | undefined;
      if (selected) setInboxFilters({ statusFilter: selected });
    },
    defaultValue: [active],
  });

  const handleSelectAll = () => {
    setInboxFilters({ statusFilter: FaxTypes.FaxStatusFilter.ALL });
    props.setValue([FaxTypes.FaxStatusFilter.ALL]);
  };

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
        '.status-filter-chips-group': {
          paddingLeft: theme.spacing(0.75),
          paddingRight: theme.spacing(0.75),
          height: '32px',
          ':focus': { outline: 'none' },
        },
        '.status-filter-chips-group.selected': {
          border: `1px solid ${theme.colors.primary50}`,
        },
      }}
    >
      <Chip
        css={{
          cursor: 'pointer',
          flexShrink: 0,
          ...(isAllSelected
            ? {
                color: theme.colors.white,
                backgroundColor: theme.colors.primary50,
                border: `1px solid ${theme.colors.primary50}`,
              }
            : {}),
        }}
        className='status-filter-chips-group'
        onClick={handleSelectAll}
        size='large'
        variant={isAllSelected ? 'primaryDark' : 'outline'}
        trackingId={`${FaxPrefixes.List}-status-filter-all`}
      >
        <Text size='medium' color={isAllSelected ? 'white' : 'default'}>
          {t('All')}
        </Text>
      </Chip>
      <div
        css={{
          display: 'block',
          margin: theme.spacing(4 / 8),
          height: theme.spacing(2),
          borderLeft: `1px solid ${theme.colors.neutral20}`,
          flexShrink: 0,
        }}
      />
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(6 / 8),
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <SwitchChipGroup style={{ gap: theme.spacing(6 / 8) }} singleSelect {...props}>
          {FILTER_OPTIONS.map(({ value, label }) => (
            <SwitchChipGroup.Option
              key={value}
              value={value}
              className={`status-filter-chips-group ${props.value.includes(value) && !isAllSelected ? 'selected' : ''}`}
              trackingId={`${FaxPrefixes.List}-status-filter-${value}`}
            >
              <Text size='medium' color={props.value.includes(value) && !isAllSelected ? 'white' : 'default'}>
                {label}
              </Text>
            </SwitchChipGroup.Option>
          ))}
        </SwitchChipGroup>
      </div>
    </div>
  );
};
